import React, { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

import { getInsuranceByCountryCode, useBrowser, useListReviewsQuery } from "@stepapp/core";
import { type BundleCheckList, ReviewSortFields, SortDirection } from "@stepapp/core/apiTypes";
import {
  Block,
  Button,
  CollapsibleChecklist,
  Divider,
  SvgIcon,
  TestimonialItem,
  TestimonialPlaceholder,
  Typography,
  palette,
  spacing,
} from "@stepapp/ui";

import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { useAppLinks } from "../../hooks";
import { AppStorage } from "../../services/AppStorage";

type Props = {
  checklist?: BundleCheckList[];
  bundleId: string;
};

const ReservationPlaceholderWeb = ({ checklist = [], bundleId }: Props) => {
  const { t } = useTranslation();
  const countryCode = AppStorage().get("countryCode");
  const { StepCover } = useAppLinks();
  const openStepCover = useBrowser(StepCover);
  const insurance = getInsuranceByCountryCode(countryCode);
  const { navigate } = useRouter();

  const { data, loading: loadingReview } = useListReviewsQuery({
    variables: {
      filter: { rating: { eq: 5 }, bundleId: { eq: bundleId } },
      sorting: {
        direction: SortDirection.Desc,
        field: ReviewSortFields.ReservationStartedAt,
      },
    },
  });

  const filteredReviews = useMemo(
    () =>
      data?.reviews?.edges?.filter(
        ({ node }) =>
          !!node?.comment && !node.customerName.includes("test") && !node.comment.includes("test"),
      ),
    [data],
  );

  const review = filteredReviews?.[0]?.node;
  const showReviewPlaceholder = loadingReview && !review?.id;

  const handleOpenReviewsPage = useCallback(() => {
    navigate("/reviews");
  }, [navigate]);

  return (
    <Block marginTop={6.5} style={styles.container}>
      <Block width={48}>
        {showReviewPlaceholder && <TestimonialPlaceholder width={48} marginTop={2} />}

        {review && (
          <>
            <Typography variant="newH3">{t("screens.service.happyCustomers")}</Typography>
            <TestimonialItem
              containerStyle={styles.testimonial}
              review={review}
              t={t}
              openReviews={handleOpenReviewsPage}
            />
            <Divider />
          </>
        )}

        <Block paddingTop={1.5}>
          <Block row>
            <Typography variant="newH3">{t("screens.service.serviceProtects")}</Typography>
            <Block marginLeft={0.5}>
              <SvgIcon name="stepcover" />
            </Block>
          </Block>

          <Block alignCenter row paddingTop={2}>
            <Block paddingRight={1}>
              <SvgIcon color={palette.font.error} name="insurance" size={36} />
            </Block>

            <Typography variant="lightLabel">
              {t("screens.service.serviceInsurance", { amount: insurance })}
              <Button
                label={t("screens.service.seeDetails")}
                onPress={openStepCover}
                textStyle={styles.link}
                textVariant="lightLabel"
                type="inlineLink"
              />
            </Typography>
          </Block>
        </Block>

        {checklist?.length > 0 && (
          <Block paddingTop={1}>
            <Divider />

            <Block paddingVertical={1}>
              <Typography variant="newH3">{t("screens.service.whatServiceIncludes")}</Typography>
            </Block>

            <CollapsibleChecklist checklist={checklist} />
          </Block>
        )}
      </Block>
    </Block>
  );
};

export default React.memo(ReservationPlaceholderWeb);

const styles = StyleSheet.create({
  container: {
    marginLeft: "auto",
    paddingLeft: 12,
  },
  link: {
    color: palette.font.primary,
    fontSize: 13,
  },
  testimonial: {
    marginVertical: spacing.unit * 2,
  },
});
